import React, { Fragment, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useCategories } from "../../../hooks/apiHooks/useCategories";
import { saveContentInterests } from "../../../config/apisauce";
import useNotiStack from "../../../hooks/useNotiStack";
import { CheckboxGroup, CategorySection } from "../../../components/Inputs/InterestMultiSelectCheckbox";
import { EditProfileButtons } from "./EditProfileButtons";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CONTENT_INTEREST_NSFW, CONTENT_INTEREST_SFW } from "../../../enums/generalEnums";

const validationSchema = Yup.object({
  content_interest_sfw: Yup.array().test(
    "content-sfw-required",
    "Please select at least one SFW interest or mark 'Not Interested'.",
    (value) => value.length > 0
  ),
  content_interest_nsfw: Yup.array().test(
    "content-nsfw-required",
    "Please select at least one NSFW interest or mark 'Not Interested'.",
    (value) => value.length > 0
  ),
});

export const EditContentInterestForm = ({ creatorDetails, handleClose, refetchDetails }) => {
  const { success } = useNotiStack();
  const [loading, setLoading] = useState(false);
  const [showContentSFW, setShowContentSFW] = useState(true);

  // prettier-ignore
  const { data: categories = [], isLoading, error } = useCategories({
    type: [CONTENT_INTEREST_SFW, CONTENT_INTEREST_NSFW],
  });

  // Group categories by type
  const groupedCategories = categories.reduce((acc, category) => {
    acc[category.type] = acc[category.type] || [];
    acc[category.type].push(category);
    return acc;
  }, {});

  const formik = useFormik({
    initialValues: {
      content_interest_sfw: creatorDetails?.user?.content_interest?.filter((item) => item?.type === CONTENT_INTEREST_SFW) || [],
      content_interest_nsfw:
        creatorDetails?.user?.content_interest?.filter((item) => item?.type === CONTENT_INTEREST_NSFW) || [],
    },
    validationSchema,
    onSubmit: async (values, { setErrors }) => {
      setLoading(true);
      try {
        const isSfwNotInterested = values.content_interest_sfw.length === 1 && values.content_interest_sfw[0] === "";
        const isNsfwNotInterested = values.content_interest_nsfw.length === 1 && values.content_interest_nsfw[0] === "";

        let categories_id = [];

        if (isSfwNotInterested && isNsfwNotInterested) {
          categories_id = [null, null];
        } else {
          categories_id = [...values.content_interest_sfw, ...values.content_interest_nsfw]
            .filter((item) => item !== "")
            .map((item) => item.id);
        }

        const params = {
          user_id: creatorDetails?.user_id,
          categories_id,
        };

        const response = await saveContentInterests(params);
        if (response.ok) {
          success("Successfully updated content interests!");
          handleClose();
          refetchDetails();
        } else {
          setErrors({ submit: "Failed to save interests. Please try again." });
        }
      } catch (error) {
        setErrors({ submit: error.message });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCheckboxChange = (interest, categoryType) => {
    let selectedCategories = formik.values[categoryType] || [];
    selectedCategories = selectedCategories.filter((item) => item !== "");
    const isSelected = selectedCategories.some((item) => item?.id === interest.id);
    const updated = isSelected
      ? selectedCategories.filter((item) => item.id !== interest.id)
      : [...selectedCategories, interest];
    formik.setFieldValue(categoryType, updated);
  };

  const handleNotInterestedChange = (checked, categoryType) => {
    if (checked) {
      formik.setFieldValue(categoryType, [""]);
    } else {
      const selectedCategories = formik.values[categoryType] || [];
      const updated = selectedCategories.filter((item) => item !== "");
      formik.setFieldValue(categoryType, updated);
    }
  };

  // Calculate selected IDs for button disabling
  const contentInterestSelectedIds = [...formik.values.content_interest_sfw, ...formik.values.content_interest_nsfw]
    .filter((item) => item !== "")
    .map((item) => item.id);

  if (isLoading) {
    return <Box>Loading categories...</Box>;
  }

  if (error) {
    return <Box color="error">Failed to load categories. Please try again.</Box>;
  }

  return (
    <Fragment>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        <CategorySection
          title="Content Interest"
          error={formik.errors.content_interest_sfw || formik.errors.content_interest_nsfw}
          showSFW={showContentSFW}
          setShowSFW={setShowContentSFW}
          categoryType={showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW}
        />

        <CheckboxGroup
          categories={groupedCategories[showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW] || []}
          selectedCategories={formik.values[showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW] || []}
          handleCheckboxChange={handleCheckboxChange}
          handleNotInterestedChange={handleNotInterestedChange}
          categoryType={showContentSFW ? CONTENT_INTEREST_SFW : CONTENT_INTEREST_NSFW}
        />

        {formik.errors.submit && <Typography color="error">{formik.errors.submit}</Typography>}

        <EditProfileButtons
          handCancel={handleClose}
          handleSubmit={formik.handleSubmit}
          disabled={
            contentInterestSelectedIds.length < 1 &&
            !formik.values.content_interest_sfw.includes("") &&
            !formik.values.content_interest_nsfw.includes("")
          }
          loading={loading}
        />
      </Box>
    </Fragment>
  );
};
